import { useEffect } from "react";
import { SITE } from "../site";

const setMeta = (key, content, isProperty = false) => {
    if (!content) return;
    const attr = isProperty ? "property" : "name";
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
};

const removeMeta = (key, isProperty = false) => {
    const attr = isProperty ? "property" : "name";
    document.head.querySelectorAll(`meta[${attr}="${key}"]`).forEach((el) => el.remove());
};

const absolute = (src) => (src && !src.startsWith("http") ? `${SITE.url}${src}` : src);

export const Seo = ({
    title,
    description,
    path = "/",
    noindex = false,
    crumbs = null,
    image = null,
    type = "website",
    publishedTime = null,
    modifiedTime = null,
    jsonLd = null,
    alternates = null, // { en: "/blog/x", hi: "/blog/x-hi" }
    lang = null,
}) => {
    useEffect(() => {
        if (title) document.title = title;
        setMeta("description", description);
        setMeta("robots", noindex ? "noindex, nofollow" : "index, follow");
        setMeta("og:title", title, true);
        setMeta("og:description", description, true);
        setMeta("og:url", `${SITE.url}${path}`, true);
        setMeta("og:type", type, true);
        let link = document.head.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement("link");
            link.rel = "canonical";
            document.head.appendChild(link);
        }
        link.href = `${SITE.url}${path}`;

        const cleanup = [];

        if (image) {
            const img = absolute(image);
            setMeta("og:image", img, true);
            setMeta("twitter:card", "summary_large_image");
            setMeta("twitter:image", img);
            setMeta("twitter:title", title);
            setMeta("twitter:description", description);
            cleanup.push(() => {
                removeMeta("og:image", true);
                removeMeta("twitter:image");
                removeMeta("twitter:card");
            });
        }
        if (type === "article") {
            if (publishedTime) setMeta("article:published_time", publishedTime, true);
            if (modifiedTime) setMeta("article:modified_time", modifiedTime, true);
            cleanup.push(() => {
                removeMeta("article:published_time", true);
                removeMeta("article:modified_time", true);
                removeMeta("og:type", true);
            });
        }

        // hreflang alternates for bilingual articles
        const hreflangEls = [];
        if (alternates) {
            Object.entries(alternates).forEach(([hl, p]) => {
                if (!p) return;
                const l = document.createElement("link");
                l.rel = "alternate";
                l.hreflang = hl;
                l.href = `${SITE.url}${p}`;
                document.head.appendChild(l);
                hreflangEls.push(l);
            });
            if (alternates.en) {
                const l = document.createElement("link");
                l.rel = "alternate";
                l.hreflang = "x-default";
                l.href = `${SITE.url}${alternates.en}`;
                document.head.appendChild(l);
                hreflangEls.push(l);
            }
        }
        cleanup.push(() => hreflangEls.forEach((l) => l.remove()));

        const prevLang = document.documentElement.lang;
        if (lang) document.documentElement.lang = lang;
        if (lang) cleanup.push(() => (document.documentElement.lang = prevLang));

        // structured data: breadcrumbs + optional article JSON-LD
        const scripts = [];
        const addJsonLd = (id, obj) => {
            const s = document.createElement("script");
            s.id = id;
            s.type = "application/ld+json";
            s.textContent = JSON.stringify(obj);
            document.head.appendChild(s);
            scripts.push(s);
        };
        const oldCrumbs = document.getElementById("seo-breadcrumbs");
        if (oldCrumbs) oldCrumbs.remove();
        if (crumbs?.length) {
            addJsonLd("seo-breadcrumbs", {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: crumbs.map((c, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    name: c.name,
                    item: `${SITE.url}${c.path}`,
                })),
            });
        }
        if (jsonLd) {
            (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).forEach((obj, i) => addJsonLd(`seo-jsonld-${i}`, obj));
        }
        cleanup.push(() => scripts.forEach((s) => s.remove()));

        return () => cleanup.forEach((fn) => fn());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, description, path, noindex, JSON.stringify(crumbs), image, type, publishedTime, modifiedTime, JSON.stringify(jsonLd), JSON.stringify(alternates), lang]);
    return null;
};
